import demoCsv from "../../data/seed/Baseline_Events_Log_Export.csv?raw";
import { parseBaselineCsv, summarizeUpload } from "../lib/baseline/parser";
import { makeId } from "../lib/utils";
import type {
  AppState,
  Company,
  CompanyMember,
  CompanyRole,
  ParsedEvent,
  ShareLink,
  ShareLinkType,
  Upload,
  UploadSummary,
  User,
} from "../types/domain";

const storageKey = "baselineboard:v1";
const databaseName = "baselineboard";
const databaseVersion = 1;
const reportStoreName = "uploadReports";
const demoEmail = "demo@baselineboard.app";
const demoPassword = "BaselineBoardDemo123!";
const demoCompanyId = "company_demo_humphrey";
const demoUserId = "user_demo";
const demoUploadId = "upload_demo_main_office";

interface StoredUploadReport {
  uploadId: string;
  originalFileContent?: string;
  events: ParsedEvent[];
  updatedAt?: string;
}

export interface UploadInput {
  fileName: string;
  fileSizeBytes: number;
  csvText: string;
  uploadedByUserId: string;
  companyId: string;
  controllerName?: string;
  projectName?: string;
  controllerAddress?: string;
  latitude?: number;
  longitude?: number;
}

export class LocalRepository {
  private state: AppState;
  readonly ready: Promise<void>;

  constructor() {
    this.state = this.load();
    this.ready = this.hydrateLargeReports();
  }

  snapshot() {
    return structuredClone(this.state);
  }

  currentUser() {
    return this.state.users.find((user) => user.userId === this.state.currentUserId);
  }

  currentCompany() {
    return this.state.companies.find((company) => company.companyId === this.state.currentCompanyId);
  }

  demoCredentials() {
    return { email: demoEmail, password: demoPassword };
  }

  signIn(email: string, password: string) {
    const normalized = email.trim().toLowerCase();
    const user = this.state.users.find((item) => item.email.toLowerCase() === normalized);
    if (!user || this.state.credentials[normalized] !== password) {
      throw new Error("That email and password combination was not found.");
    }
    const activeMembership = this.state.members.find(
      (member) => member.userId === user.userId && member.status === "ACTIVE",
    );
    this.state.currentUserId = user.userId;
    this.state.currentCompanyId = activeMembership?.companyId;
    void this.persist();
    return user;
  }

  signOut() {
    this.state.currentUserId = undefined;
    this.state.currentCompanyId = undefined;
    void this.persist();
  }

  signUp(email: string, password: string, name?: string) {
    const normalized = email.trim().toLowerCase();
    const existingUser = this.state.users.find((user) => user.email.toLowerCase() === normalized);
    if (existingUser?.emailVerified) {
      throw new Error("An account already exists for that email.");
    }
    if (existingUser) {
      if (name?.trim()) {
        existingUser.name = name.trim();
        existingUser.updatedAt = localIsoNow();
      }
      this.state.credentials[normalized] = password;
      this.state.currentUserId = existingUser.userId;
      this.state.currentCompanyId = undefined;
      void this.persist();
      return existingUser;
    }
    const now = localIsoNow();
    const user: User = {
      userId: makeId("user"),
      email: normalized,
      name: name?.trim() || undefined,
      emailVerified: false,
      createdAt: now,
      updatedAt: now,
    };
    this.state.users.push(user);
    this.state.credentials[normalized] = password;
    this.state.currentUserId = user.userId;
    this.state.currentCompanyId = undefined;
    void this.persist();
    return user;
  }

  verifyEmail() {
    const user = this.currentUser();
    if (!user) throw new Error("Sign in before verifying an email address.");
    user.emailVerified = true;
    user.updatedAt = localIsoNow();
    void this.persist();
    return user;
  }

  requestPasswordReset(email: string) {
    const normalized = email.trim().toLowerCase();
    if (!this.state.users.some((user) => user.email.toLowerCase() === normalized)) {
      throw new Error("No account was found for that email address.");
    }
    return true;
  }

  createCompany(name: string, userName?: string) {
    const user = this.currentUser();
    if (!user) throw new Error("Sign in before creating a company workspace.");
    const now = localIsoNow();
    if (userName?.trim()) {
      user.name = userName.trim();
      user.updatedAt = now;
    }
    const company: Company = {
      companyId: makeId("company"),
      name: name.trim(),
      createdByUserId: user.userId,
      createdAt: now,
      updatedAt: now,
    };
    const member: CompanyMember = {
      companyId: company.companyId,
      userId: user.userId,
      role: "OWNER",
      status: "ACTIVE",
      createdAt: now,
      updatedAt: now,
    };
    this.state.companies.push(company);
    this.state.members.push(member);
    this.state.currentCompanyId = company.companyId;
    void this.persist();
    return company;
  }

  updateCompany(companyId: string, patch: Pick<Company, "name">) {
    const company = this.requireCompany(companyId);
    company.name = patch.name.trim();
    company.updatedAt = localIsoNow();
    void this.persist();
    return company;
  }

  getCompanyUploads(companyId: string) {
    this.requireMembership(companyId);
    return this.state.uploads
      .filter((upload) => upload.companyId === companyId)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  getUpload(uploadId: string) {
    const upload = this.state.uploads.find((item) => item.uploadId === uploadId);
    if (!upload) return undefined;
    this.requireMembership(upload.companyId);
    return upload;
  }

  getUploadEvents(uploadId: string) {
    const upload = this.getUpload(uploadId);
    if (!upload) return [];
    return this.state.events.filter((event) => event.uploadId === upload.uploadId);
  }

  getUploadSummary(uploadId: string): UploadSummary {
    return summarizeUpload(uploadId, this.getUploadEvents(uploadId));
  }

  async createUpload(input: UploadInput) {
    this.assertCanUpload(input.companyId);
    validateCsvFile(input.fileName, input.fileSizeBytes);
    const now = localIsoNow();
    const uploadId = makeId("upload");
    const baseUpload: Upload = {
      uploadId,
      companyId: input.companyId,
      uploadedByUserId: input.uploadedByUserId,
      originalFileName: input.fileName,
      originalFileS3Key: `local/${input.companyId}/${uploadId}/${input.fileName}`,
      originalFileContent: input.csvText,
      fileSizeBytes: input.fileSizeBytes,
      rowCount: 0,
      controllerName: input.controllerName?.trim() || undefined,
      projectName: input.projectName?.trim() || undefined,
      controllerAddress: input.controllerAddress?.trim() || undefined,
      latitude: input.latitude,
      longitude: input.longitude,
      status: "PROCESSING",
      criticalIssueCount: 0,
      warningCount: 0,
      wateringEventCount: 0,
      communicationEventCount: 0,
      configurationChangeCount: 0,
      createdAt: now,
      updatedAt: now,
    };
    this.state.uploads.push(baseUpload);

    try {
      const parsed = parseBaselineCsv(input.csvText, {
        uploadId,
        companyId: input.companyId,
        createdAt: now,
      });
      Object.assign(baseUpload, {
        ...parsed.stats,
        status: "PROCESSED",
        updatedAt: localIsoNow(),
      });
      this.state.events.push(...parsed.events);
      await this.persist();
      return baseUpload;
    } catch (error) {
      baseUpload.status = "FAILED";
      baseUpload.failureReason =
        error instanceof Error
          ? error.message
          : "We could not read this file as a Baseline event CSV.";
      baseUpload.updatedAt = localIsoNow();
      await this.persist();
      throw new Error(baseUpload.failureReason);
    }
  }

  updateUpload(uploadId: string, patch: Partial<Upload>) {
    const upload = this.getUpload(uploadId);
    if (!upload) throw new Error("Upload not found.");
    Object.assign(upload, {
      controllerName: patch.controllerName?.trim() || undefined,
      projectName: patch.projectName?.trim() || undefined,
      controllerAddress: patch.controllerAddress?.trim() || undefined,
      latitude: patch.latitude,
      longitude: patch.longitude,
      updatedAt: localIsoNow(),
    });
    void this.persist();
    return upload;
  }

  deleteUpload(uploadId: string) {
    const upload = this.getUpload(uploadId);
    if (!upload) throw new Error("Upload not found.");
    this.assertCanUpload(upload.companyId);
    this.state.uploads = this.state.uploads.filter((item) => item.uploadId !== uploadId);
    this.state.events = this.state.events.filter((event) => event.uploadId !== uploadId);
    this.state.shareLinks = this.state.shareLinks.filter((link) => link.uploadId !== uploadId);
    this.state.deletedUploadIds = Array.from(new Set([...(this.state.deletedUploadIds ?? []), uploadId]));
    void this.persist();
  }

  createShareLink(input: {
    uploadId: string;
    type: ShareLinkType;
    hideSerialNumbers?: boolean;
    expiresAt?: string;
  }) {
    const upload = this.getUpload(input.uploadId);
    const user = this.currentUser();
    if (!upload || !user) throw new Error("Upload not found.");
    this.requireMembership(upload.companyId);
    const now = localIsoNow();
    const isClientSafe = input.type === "CLIENT_SAFE";
    const link: ShareLink = {
      shareId: makeId(input.type === "SUPPORT" ? "support" : input.type === "CLIENT_SAFE" ? "client" : "share"),
      uploadId: upload.uploadId,
      companyId: upload.companyId,
      createdByUserId: user.userId,
      type: input.type,
      isActive: true,
      expiresAt: input.expiresAt,
      hideSerialNumbers: input.hideSerialNumbers ?? isClientSafe,
      hideUserInfo: isClientSafe,
      hideCompanyInternalNotes: isClientSafe,
      createdAt: now,
      updatedAt: now,
    };
    this.state.shareLinks.push(link);
    void this.persist();
    return link;
  }

  disableShareLink(shareId: string) {
    const link = this.state.shareLinks.find((item) => item.shareId === shareId);
    if (!link) throw new Error("Share link not found.");
    this.requireMembership(link.companyId);
    link.isActive = false;
    link.updatedAt = localIsoNow();
    void this.persist();
    return link;
  }

  getShareLinks(uploadId?: string) {
    return this.state.shareLinks.filter((link) => !uploadId || link.uploadId === uploadId);
  }

  getPublicShare(shareId: string) {
    const link = this.state.shareLinks.find((item) => item.shareId === shareId);
    if (!link || !link.isActive) return undefined;
    if (link.expiresAt && link.expiresAt < localIsoNow()) return undefined;
    const upload = this.state.uploads.find((item) => item.uploadId === link.uploadId);
    if (!upload) return undefined;
    const events = this.state.events.filter((event) => event.uploadId === upload.uploadId);
    const company = this.state.companies.find((item) => item.companyId === upload.companyId);
    return { link, upload, events, summary: summarizeUpload(upload.uploadId, events), company };
  }

  getMembers(companyId: string) {
    this.requireMembership(companyId);
    return this.state.members.filter((member) => member.companyId === companyId);
  }

  getMemberUsers(companyId: string) {
    return this.getMembers(companyId).map((member) => ({
      member,
      user: this.state.users.find((user) => user.userId === member.userId),
    }));
  }

  inviteMember(companyId: string, email: string, role: CompanyRole) {
    this.assertCanManageMembers(companyId);
    const now = localIsoNow();
    const normalized = email.trim().toLowerCase();
    let user = this.state.users.find((item) => item.email.toLowerCase() === normalized);
    if (!user) {
      user = {
        userId: makeId("user"),
        email: normalized,
        emailVerified: false,
        createdAt: now,
        updatedAt: now,
      };
      this.state.users.push(user);
    }
    const existing = this.state.members.find(
      (member) => member.companyId === companyId && member.userId === user!.userId,
    );
    if (existing) {
      existing.role = role;
      existing.status = "INVITED";
      existing.invitedEmail = normalized;
      existing.updatedAt = now;
      void this.persist();
      return existing;
    }
    const member: CompanyMember = {
      companyId,
      userId: user.userId,
      role,
      status: "INVITED",
      invitedEmail: normalized,
      createdAt: now,
      updatedAt: now,
    };
    this.state.members.push(member);
    void this.persist();
    return member;
  }

  updateMember(companyId: string, userId: string, patch: Partial<CompanyMember>) {
    this.assertCanManageMembers(companyId);
    const member = this.state.members.find(
      (item) => item.companyId === companyId && item.userId === userId,
    );
    if (!member) throw new Error("Team member not found.");
    if (patch.role) member.role = patch.role;
    if (patch.status) member.status = patch.status;
    member.updatedAt = localIsoNow();
    void this.persist();
    return member;
  }

  activeMember(companyId: string, userId = this.state.currentUserId) {
    return this.state.members.find(
      (member) =>
        member.companyId === companyId && member.userId === userId && member.status === "ACTIVE",
    );
  }

  private requireCompany(companyId: string) {
    const company = this.state.companies.find((item) => item.companyId === companyId);
    if (!company) throw new Error("Company not found.");
    return company;
  }

  private requireMembership(companyId: string) {
    const user = this.currentUser();
    if (!user) throw new Error("Sign in to access this workspace.");
    const member = this.activeMember(companyId, user.userId);
    if (!member) throw new Error("You do not have access to this company workspace.");
    return member;
  }

  private assertCanUpload(companyId: string) {
    const member = this.requireMembership(companyId);
    if (member.role === "VIEWER") throw new Error("Viewers cannot upload event files.");
  }

  private assertCanManageMembers(companyId: string) {
    const member = this.requireMembership(companyId);
    if (!["OWNER", "ADMIN"].includes(member.role)) {
      throw new Error("Only owners and admins can manage the company team.");
    }
  }

  private load(): AppState {
    const stored = readStoredState();
    if (stored) return ensureDemoSeed(stored);
    return ensureDemoSeed(emptyState());
  }

  private async hydrateLargeReports() {
    const reports = await loadStoredReports();
    const reportsByUploadId = new Map(reports.map((report) => [report.uploadId, report]));
    const currentEventsByUploadId = groupEventsByUpload(this.state.events);

    this.state.uploads = this.state.uploads.map((upload) => {
      const report = reportsByUploadId.get(upload.uploadId);
      if (!report) return upload;
      return {
        ...upload,
        originalFileContent: report.originalFileContent,
      };
    });

    this.state.events = this.state.uploads.flatMap((upload) => {
      const report = reportsByUploadId.get(upload.uploadId);
      return report ? report.events : currentEventsByUploadId.get(upload.uploadId) ?? [];
    });

    await this.persist();
  }

  private async persist() {
    persistMetadataState(this.state);
    await persistReports(this.state);
  }
}

function readStoredState() {
  const stored = readStorageItem("local") ?? readStorageItem("session");
  if (!stored) return undefined;
  try {
    return JSON.parse(stored) as AppState;
  } catch {
    removeStorageItem("local");
    removeStorageItem("session");
    return undefined;
  }
}

function readStorageItem(type: "local" | "session") {
  try {
    const storage = type === "local" ? localStorage : sessionStorage;
    return storage.getItem(storageKey);
  } catch {
    return undefined;
  }
}

function removeStorageItem(type: "local" | "session") {
  try {
    const storage = type === "local" ? localStorage : sessionStorage;
    storage.removeItem(storageKey);
  } catch {
    // Storage can be unavailable in private contexts; in-memory state still works.
  }
}

function persistMetadataState(state: AppState) {
  const serialized = JSON.stringify(compactStateForStorage(state));
  if (writeStorageItem("local", serialized)) return;
  removeStorageItem("local");
  if (writeStorageItem("local", serialized)) return;
  removeStorageItem("session");
  writeStorageItem("session", serialized);
}

function writeStorageItem(type: "local" | "session", value: string) {
  try {
    const storage = type === "local" ? localStorage : sessionStorage;
    storage.setItem(storageKey, value);
    return true;
  } catch {
    return false;
  }
}

function compactStateForStorage(state: AppState): AppState {
  return {
    ...state,
    uploads: state.uploads.map((upload) => {
      const { originalFileContent: _originalFileContent, ...uploadMetadata } = upload;
      return uploadMetadata;
    }),
    events: [],
  };
}

async function loadStoredReports() {
  const db = await openReportsDatabase();
  if (!db) return [];
  try {
    return await getAllReports(db);
  } finally {
    db.close();
  }
}

async function persistReports(state: AppState) {
  const db = await openReportsDatabase();
  if (!db) return;
  try {
    await writeReports(db, state);
  } finally {
    db.close();
  }
}

function openReportsDatabase(): Promise<IDBDatabase | undefined> {
  if (typeof indexedDB === "undefined") return Promise.resolve(undefined);

  return new Promise((resolve) => {
    const request = indexedDB.open(databaseName, databaseVersion);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(reportStoreName)) {
        db.createObjectStore(reportStoreName, { keyPath: "uploadId" });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(undefined);
    request.onblocked = () => resolve(undefined);
  });
}

function getAllReports(db: IDBDatabase): Promise<StoredUploadReport[]> {
  return new Promise((resolve) => {
    const transaction = db.transaction(reportStoreName, "readonly");
    const store = transaction.objectStore(reportStoreName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result as StoredUploadReport[]);
    request.onerror = () => resolve([]);
    transaction.onerror = () => resolve([]);
    transaction.onabort = () => resolve([]);
  });
}

function writeReports(db: IDBDatabase, state: AppState): Promise<void> {
  return new Promise((resolve) => {
    const uploadIds = new Set(state.uploads.map((upload) => upload.uploadId));
    const eventsByUploadId = groupEventsByUpload(state.events);
    const transaction = db.transaction(reportStoreName, "readwrite");
    const store = transaction.objectStore(reportStoreName);
    const keyRequest = store.getAllKeys();

    for (const upload of state.uploads) {
      const report: StoredUploadReport = {
        uploadId: upload.uploadId,
        originalFileContent: upload.originalFileContent,
        events: eventsByUploadId.get(upload.uploadId) ?? [],
        updatedAt: upload.updatedAt,
      };
      store.put(report);
    }

    keyRequest.onsuccess = () => {
      for (const key of keyRequest.result) {
        const uploadId = String(key);
        if (!uploadIds.has(uploadId)) store.delete(uploadId);
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => resolve();
    transaction.onabort = () => resolve();
  });
}

function groupEventsByUpload(events: ParsedEvent[]) {
  const eventsByUploadId = new Map<string, ParsedEvent[]>();
  for (const event of events) {
    const uploadEvents = eventsByUploadId.get(event.uploadId) ?? [];
    uploadEvents.push(event);
    eventsByUploadId.set(event.uploadId, uploadEvents);
  }
  return eventsByUploadId;
}

export function validateCsvFile(fileName: string, fileSizeBytes: number) {
  if (!fileName.toLowerCase().endsWith(".csv")) {
    throw new Error("Please upload the Evnt CSV file from the controller export folder.");
  }
  if (fileSizeBytes > 10 * 1024 * 1024) {
    throw new Error("CSV files are limited to 10 MB in this version.");
  }
  if (fileSizeBytes === 0) {
    throw new Error("The selected CSV file is empty.");
  }
}

function emptyState(): AppState {
  return {
    users: [],
    companies: [],
    members: [],
    uploads: [],
    events: [],
    shareLinks: [],
    deletedUploadIds: [],
    credentials: {},
  };
}

function ensureDemoSeed(state: AppState): AppState {
  const now = "2026-06-20T12:00:00";
  state.deletedUploadIds ??= [];
  if (!state.users.some((user) => user.userId === demoUserId)) {
    state.users.push({
      userId: demoUserId,
      email: demoEmail,
      name: "Demo User",
      emailVerified: true,
      createdAt: now,
      updatedAt: now,
    });
  }
  state.credentials[demoEmail] = demoPassword;

  if (!state.companies.some((company) => company.companyId === demoCompanyId)) {
    state.companies.push({
      companyId: demoCompanyId,
      name: "Humphrey Irrigation",
      createdByUserId: demoUserId,
      createdAt: now,
      updatedAt: now,
    });
  }

  if (
    !state.members.some(
      (member) => member.companyId === demoCompanyId && member.userId === demoUserId,
    )
  ) {
    state.members.push({
      companyId: demoCompanyId,
      userId: demoUserId,
      role: "OWNER",
      status: "ACTIVE",
      createdAt: now,
      updatedAt: now,
    });
  }

  if (
    !state.deletedUploadIds.includes(demoUploadId) &&
    !state.uploads.some((upload) => upload.uploadId === demoUploadId)
  ) {
    const parsed = parseBaselineCsv(demoCsv, {
      uploadId: demoUploadId,
      companyId: demoCompanyId,
      createdAt: now,
    });
    state.uploads.push({
      uploadId: demoUploadId,
      companyId: demoCompanyId,
      uploadedByUserId: demoUserId,
      originalFileName: "Baseline_Events_Log_Export.csv",
      originalFileS3Key: `seed/${demoUploadId}/Baseline_Events_Log_Export.csv`,
      originalFileContent: demoCsv,
      fileSizeBytes: new Blob([demoCsv]).size,
      controllerName: "Main Office Controller",
      projectName: "Main Office Controller",
      rowCount: parsed.rowCount,
      dateRangeStart: parsed.dateRangeStart,
      dateRangeEnd: parsed.dateRangeEnd,
      status: "PROCESSED",
      criticalIssueCount: parsed.stats.criticalIssueCount,
      warningCount: parsed.stats.warningCount,
      wateringEventCount: parsed.stats.wateringEventCount,
      communicationEventCount: parsed.stats.communicationEventCount,
      configurationChangeCount: parsed.stats.configurationChangeCount,
      createdAt: now,
      updatedAt: now,
    });
    state.events.push(...parsed.events);
  }

  return state;
}

function localIsoNow() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}T${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
}

export function visibleEventForShare(event: ParsedEvent, hideSerialNumbers: boolean): ParsedEvent {
  if (!hideSerialNumbers) return event;
  return {
    ...event,
    deviceSerialNumber: event.deviceSerialNumber ? "Hidden" : undefined,
    detailEntries: event.detailEntries.map((detail) =>
      detail.code === "SN" ? { ...detail, rawValue: "Hidden", values: ["Hidden"] } : detail,
    ),
    rawDetails: {
      ...event.rawDetails,
      ...(event.rawDetails.SN ? { SN: "Hidden" } : {}),
    },
  };
}
